import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useForm, Controller } from 'react-hook-form'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Button } from '../../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import PhoneInput from '../../leevo_ui/PhoneInput/PhoneInput'

function extractSignupErrorMessage(error) {
  return error?.response?.data?.errors?.[0] || 'An error occurred. Please try again.'
}

function isExistingUserSignupError(message) {
  const normalizedMessage = message.toLowerCase()

  return (
    normalizedMessage.includes('email has already been taken') || normalizedMessage.includes('email already exists')
  )
}

function Register({ onSubmit, onToggleMode, facilityName, demo = false }) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorState, setErrorState] = useState({ message: '', isExistingUser: false })

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      fullName: '',
      preferredName: '',
      email: '',
      phone: '',
      password: '',
      passwordConfirmation: ''
    }
  })

  const password = watch('password')

  async function handleFormSubmit(data) {
    setIsSubmitting(true)
    setErrorState({ message: '', isExistingUser: false })

    try {
      await onSubmit(data)
    } catch (err) {
      const message = extractSignupErrorMessage(err)
      const isExistingUser = isExistingUserSignupError(message)

      setErrorState({
        message: isExistingUser ? 'It looks like you already have an account. Please sign in instead.' : message,
        isExistingUser
      })
      setIsSubmitting(false)
    }
  }

  function validatePhone(value) {
    if (!value) return true
    const e164Regex = /^\+?1?\d{10,}$/
    const formattedRegex = /^(\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/
    const digitsOnly = value.replace(/\D/g, '')
    return (
      e164Regex.test(value) ||
      formattedRegex.test(value) ||
      digitsOnly.length === 10 ||
      digitsOnly.length === 11 ||
      'Please enter a valid phone number'
    )
  }

  function validatePasswordConfirmation(value) {
    if (!password) return true
    return value === password || 'Passwords do not match'
  }

  if (demo) {
    return (
      <div className="mx-auto max-w-lg px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle>Explore as a parent</CardTitle>
            <CardDescription>
              Use the shared parent login supplied by Leevo: willow.parent@example.com. Your Leevo contact will provide
              the password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={onToggleMode}>Sign in with the demo login</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-[70vh] px-4 py-8">
      <Card className="w-full max-w-2xl">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Create Your Account</CardTitle>
          <CardDescription className="text-center">
            {facilityName ? `Get started with registration at ${facilityName}` : 'Create an account to get started'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            {errorState.message && (
              <div role="alert" className="p-3 rounded-md bg-red-50 border border-red-200 space-y-3">
                <p className="text-sm text-red-800">{errorState.message}</p>
                {errorState.isExistingUser && (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="button"
                      onClick={onToggleMode}
                      className="font-medium text-purple-600 hover:text-purple-700 text-left"
                    >
                      Sign in
                    </button>
                    <a href="/forgot_password" className="font-medium text-purple-600 hover:text-purple-700">
                      Forgot your password?
                    </a>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="fullName"
                  placeholder="John Smith"
                  autoComplete="name"
                  autoFocus
                  {...register('fullName', { required: 'Full name is required' })}
                  className={errors.fullName ? 'border-red-500' : ''}
                />
                {errors.fullName && <p className="text-sm text-red-600">{errors.fullName.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="preferredName">
                  Preferred Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="preferredName"
                  placeholder="John"
                  {...register('preferredName', { required: 'Preferred name is required' })}
                  className={errors.preferredName ? 'border-red-500' : ''}
                />
                {errors.preferredName && <p className="text-sm text-red-600">{errors.preferredName.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  autoComplete="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && <p className="text-sm text-red-600">{errors.email.message}</p>}
              </div>

              <div className="space-y-2">
                <Controller
                  name="phone"
                  control={control}
                  rules={{
                    required: 'Phone number is required',
                    validate: validatePhone
                  }}
                  render={({ field }) => (
                    <PhoneInput
                      id="phone"
                      label={
                        <>
                          Phone Number <span className="text-red-500">*</span>
                        </>
                      }
                      placeholder="(555) 555-5555"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      errorMessage={errors.phone?.message}
                    />
                  )}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password">
                  Password <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters'
                    }
                  })}
                  className={errors.password ? 'border-red-500' : ''}
                />
                {errors.password && <p className="text-sm text-red-600">{errors.password.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="passwordConfirmation">
                  Confirm Password <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="passwordConfirmation"
                  type="password"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  {...register('passwordConfirmation', {
                    required: 'Please confirm your password',
                    validate: validatePasswordConfirmation
                  })}
                  className={errors.passwordConfirmation ? 'border-red-500' : ''}
                />
                {errors.passwordConfirmation && (
                  <p className="text-sm text-red-600">{errors.passwordConfirmation.message}</p>
                )}
              </div>
            </div>

            <Button type="submit" disabled={isSubmitting} className="w-full bg-purple-600 hover:bg-purple-700">
              {isSubmitting ? (
                <>
                  <div className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <button
                type="button"
                onClick={onToggleMode}
                className="font-medium text-purple-600 hover:text-purple-700"
              >
                Sign in
              </button>
            </p>
            <p className="mt-2">
              <a
                data-cy="forgot-password"
                href="/forgot_password"
                className="inline-flex min-h-11 items-center rounded-sm text-sm font-medium text-purple-600 hover:text-purple-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
              >
                Forgot your password?
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

Register.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onToggleMode: PropTypes.func.isRequired,
  facilityName: PropTypes.string,
  demo: PropTypes.bool
}

export default Register
